import { FormEvent, useState } from 'react';
import MessageSquareIcon from './icons/MessageSquareIcon';
import PlusIcon from './icons/PlusIcon';
import { formatDateTime } from '../utils/dateFormat';

interface CardCommentsProps {
  card: any;
  addComment: (text: string) => Promise<boolean>;
  editComment: (commentId: number, text: string) => Promise<boolean>;
  deleteCommentWithConfirmation: (commentId: number, onSuccess: () => void) => void;
  openModal: any;
}

export function CardComments({
  card,
  addComment,
  editComment,
  deleteCommentWithConfirmation,
  openModal,
}: CardCommentsProps) {
  const [newCommentText, setNewCommentText] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editedCommentText, setEditedCommentText] = useState('');

  const handleAddComment = async (e: FormEvent) => {
    e.preventDefault();
    if (!newCommentText.trim()) return;

    const success = await addComment(newCommentText);
    if (success) {
      setNewCommentText('');
    }
  };

  const handleEditComment = (commentId: number, text: string) => {
    setEditingCommentId(commentId);
    setEditedCommentText(text);
  };

  const handleSaveComment = async (commentId: number) => {
    if (!editedCommentText.trim()) return;

    const success = await editComment(commentId, editedCommentText);
    if (success) {
      setEditingCommentId(null);
      setEditedCommentText('');
    }
  };

  const handleDeleteComment = (commentId: number) => {
    openModal((props: any) => (
      <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full">
        <h3 className="text-xl font-bold text-white mb-4">Delete Comment</h3>
        <p className="text-gray-300 mb-6">
          Are you sure you want to delete this comment? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => props.onClose()}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              deleteCommentWithConfirmation(commentId, () => {
                props.onClose();
              });
            }}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    ));
  };

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <h2 className="text-xl font-bold text-white flex items-center gap-2 mb-4">
        <span className="w-5 h-5">
          <MessageSquareIcon />
        </span>
        Comments ({card.comments?.length || 0})
      </h2>

      {/* Add Comment Form */}
      <form onSubmit={handleAddComment} className="mb-6">
        <textarea
          value={newCommentText}
          onChange={(e) => setNewCommentText(e.target.value)}
          className="w-full bg-gray-700 text-white rounded-lg p-3 min-h-24 border border-gray-600 focus:border-blue-500 focus:outline-none"
          placeholder="Write a comment..."
        />
        <button
          type="submit"
          disabled={!newCommentText.trim()}
          className="mt-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center gap-2"
        >
          <span className="w-4 h-4">
            <PlusIcon />
          </span>
          Add Comment
        </button>
      </form>

      {/* Comments List */}
      <div className="space-y-4">
        {card.comments && card.comments.length > 0 ? (
          card.comments.map((comment: any) => (
            <div key={comment.id} className="bg-gray-700 rounded-lg p-4 space-y-2">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {comment.user?.username.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div>
                    <p className="text-white font-medium">{comment.user?.username || 'Unknown'}</p>
                    <p className="text-gray-400 text-xs">{formatDateTime(comment.created_at)}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {editingCommentId === comment.id ? (
                    <button
                      onClick={() => setEditingCommentId(null)}
                      className="text-gray-400 hover:text-white text-sm"
                    >
                      Cancel
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => handleEditComment(comment.id, comment.text)}
                        className="text-gray-400 hover:text-white text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteComment(comment.id)}
                        className="text-red-400 hover:text-red-300 text-sm"
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
              {editingCommentId === comment.id ? (
                <div className="space-y-2">
                  <textarea
                    value={editedCommentText}
                    onChange={(e) => setEditedCommentText(e.target.value)}
                    className="w-full bg-gray-600 text-white rounded-lg p-2 border border-gray-500 focus:border-blue-500 focus:outline-none"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSaveComment(comment.id)}
                      className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-1 px-3 rounded transition-colors"
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-gray-300 whitespace-pre-wrap">{comment.text}</p>
              )}
            </div>
          ))
        ) : (
          <p className="text-gray-400 text-sm">No comments yet. Be the first to comment!</p>
        )}
      </div>
    </div>
  );
}
